import { Component, OnInit, inject } from '@angular/core';
import { RouterLink } from '@angular/router';
import { CounterComponent } from '../../shared/components/counter/counter.component';
import { ScrollAnimateDirective } from '../../shared/directives/scroll-animate.directive';
import { SeoService } from '../../core/services/seo.service';
import { SiteConfigService } from '../../core/config/site-config.service';
import { ParticlesComponent } from '../../shared/components/particles/particles.component';
import { ImgFallbackDirective } from '../../shared/directives/img-fallback.directive';
@Component({
  selector: 'app-about',
  standalone: true,
  imports: [RouterLink, CounterComponent, ScrollAnimateDirective, ImgFallbackDirective, ParticlesComponent],
  template: `
    <!-- Hero -->
    <section class="relative py-24 md:py-32 -mt-20 pt-32 md:pt-40 bg-surface-950 overflow-hidden grain-overlay">
      <div class="absolute inset-0 bg-gradient-to-br from-brand-950 via-surface-950 to-brand-900"></div>
      <app-particles class="absolute inset-0 z-[1] pointer-events-none" [count]="50" [connectionDistance]="130" [mouseRadius]="180" />
      <div class="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        <div class="max-w-2xl">
          <div class="section-label section-label-light">{{ page.hero.label }}</div>
          <h1 class="font-heading text-4xl md:text-5xl font-extrabold text-white leading-tight tracking-tight mb-5">
            {{ page.hero.title }} <span class="text-brand-300">{{ page.hero.highlight }}</span>
          </h1>
          <p class="text-brand-200 leading-relaxed">{{ page.hero.description }}</p>
        </div>
      </div>
    </section>

    <!-- Story -->
    <section class="py-20 md:py-28 bg-white">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
          <div appScrollAnimate data-animate="slide-left">
            <div class="aspect-[4/3] rounded-2xl overflow-hidden relative">
              @if (page.storyImage) {
                <img [src]="page.storyImage" [alt]="config.brand.name" [appImgFallback]="config.brand.name" class="w-full h-full object-cover" />
              } @else {
                <div class="w-full h-full bg-gradient-to-br from-brand-50 to-surface-100 flex items-center justify-center">
                  <svg aria-hidden="true" class="w-16 h-16 text-brand-200" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 0h.008v.008h-.008V7.5z" /></svg>
                </div>
              }
            </div>
          </div>
          <div appScrollAnimate data-animate="slide-right">
            <div class="section-label">Our Story</div>
            <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-tight mb-5">{{ page.storyHeading }}</h2>
            @for (paragraph of page.story; track $index) {
              <p class="text-surface-500 leading-relaxed" [class.mb-4]="!$last">{{ paragraph }}</p>
            }
          </div>
        </div>
      </div>
    </section>

    <!-- Stats -->
    <section class="py-14 bg-brand-700">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
          @for (stat of page.stats; track stat.label) {
            <div class="text-center">
              <div class="font-heading text-3xl md:text-4xl font-extrabold text-white"><app-counter [target]="stat.value" [suffix]="stat.suffix" [duration]="2000" /></div>
              <div class="text-sm text-brand-200 mt-1">{{ stat.label }}</div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Values -->
    <section class="py-20 md:py-28 bg-surface-50">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-14" appScrollAnimate data-animate>
          <div class="section-label justify-center">Our Values</div>
          <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-tight">What Drives Us</h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
          @for (val of page.values; track val.title; let i = $index) {
            <div appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i+1)*100"
                 class="text-center p-8 rounded-xl bg-white border border-surface-100">
              <div class="w-14 h-14 rounded-xl bg-brand-50 text-brand-600 flex items-center justify-center mx-auto mb-5 text-2xl" [innerHTML]="val.icon"></div>
              <h3 class="font-heading text-lg font-bold text-surface-900 mb-2">{{ val.title }}</h3>
              <p class="text-sm text-surface-500 leading-relaxed">{{ val.desc }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Team -->
    <section class="py-20 md:py-28 bg-white">
      <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-14" appScrollAnimate data-animate>
          <div class="section-label justify-center">Our Team</div>
          <h2 class="font-heading text-3xl md:text-4xl font-bold text-surface-900 tracking-tight">Meet the Leadership</h2>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          @for (m of page.team; track m.name; let i = $index) {
            <div appScrollAnimate [attr.data-animate]="''" [attr.data-delay]="(i+1)*100" class="text-center">
              <div class="aspect-square rounded-xl bg-surface-100 flex items-center justify-center mb-4">
                <div class="w-16 h-16 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center font-heading text-xl font-bold">{{ m.initials }}</div>
              </div>
              <h3 class="font-heading text-base font-bold text-surface-900">{{ m.name }}</h3>
              <p class="text-sm text-brand-600">{{ m.role }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="py-16 bg-brand-600">
      <div class="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
        <h2 class="font-heading text-2xl md:text-3xl font-bold text-white mb-5">Ready to Work with South Florida's Best?</h2>
        <a routerLink="/contact" class="inline-flex items-center gap-2 px-7 py-3.5 bg-white text-brand-700 font-semibold text-sm rounded-lg hover:bg-brand-50 transition-colors">
          Get Your Free Estimate
          <svg aria-hidden="true" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
        </a>
      </div>
    </section>
  `,
})
export class AboutComponent implements OnInit {
  private seo = inject(SeoService);
  config = inject(SiteConfigService);
  page = this.config.pages.about;

  ngOnInit(): void {
    this.seo.setJsonLd(this.seo.getBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'About Us', url: '/about' },
    ]));
  }
}
